//old import React, { useEffect, useState } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { CreditCard, ArrowLeft, Gift, Copy, Check } from 'lucide-react';
//old import { useNavigate } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
//import { MEMBERSHIP_FEATURES, getFeaturesForTier } from '../utils/membershipFeatures';
import { MEMBERSHIP_FEATURES, getFeaturesForTier, withTierAccess } from '../utils/membershipFeatures';
import { Button } from '../components/ui/button';
import { Card, CardBandedHeader, CardBody } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import CoachPlansSkeleton from '../components/layout/CoachPlansSkeleton';
import PlanChangeConfirm from '../components/PlanChangeConfirm';
import { actionLabel, isYearly, money, day } from '../utils/planChange';


export default function MembershipPlans() {
    const { isDarkMode } = useTheme();
    const { user, loading: authLoading } = useAuth();
        const [tiers, setTiers] = useState([]);
        const [loading, setLoading] = useState(true);
        const [subscribing, setSubscribing] = useState(null); // tier id being processed
        const [currentTierID, setCurrentTierID] = useState(null); // user's active tier
        const [billingCycle, setBillingCycle] = useState('monthly');
        const [referral, setReferral] = useState(null); // {code, canGenerate, usedCount, weeksEarned}
        const [referralCopied, setReferralCopied] = useState(false);
        const [referralLoading, setReferralLoading] = useState(false);
        // One box, two kinds of code. `promo` is a *preview* only — nothing is
        // redeemed until the user confirms a plan below.
        const [codeInput, setCodeInput] = useState('');
        const [applying, setApplying] = useState(false);
        const [codeMsg, setCodeMsg] = useState(null); // {type:'ok'|'bad', text}
        const [promo, setPromo] = useState(null); // {code, discountType, discountValue, tiers}
        const [membership, setMembership] = useState(null); // the member's own plan: what a pick does
        const [trialAvailable, setTrialAvailable] = useState(false); // a first subscription gets the plan's trial
        // The confirm window: {plan, preview} for a paid plan (what's charged today, or when it
        // starts), {plan, free: true} for the free plan
        const [change, setChange] = useState(null);
        const [changing, setChanging] = useState(false);
        const [changeError, setChangeError] = useState('');
        const navigate = useNavigate();
        // ?plan=<TierID>: [Upgrade my plan] on My Coaching -> Programs (JJ, Nov 2) comes with that plan picked
        const [searchParams, setSearchParams] = useSearchParams();
        const planFromLink = useRef(searchParams.get('plan'));

    useEffect(() => {
        // Don't fetch until auth is settled and we have a confirmed user —
        // fetching while auth is still flapping post-login triggers a
        // remount/refetch storm against /membership/me.
        if (authLoading) return;
        if (!user) { setLoading(false); return; }
        const fetchData = async () => {
            try {
                const [tiersRes, membershipRes] = await Promise.allSettled([
                    api.get('/membership/tiers'),
                    api.get('/membership/me'),
                ]);

                const m = membershipRes.status === 'fulfilled' ? membershipRes.value.data : null;
                const currentId = m?.isActive && m?.TierID ? m.TierID : null;
                if (currentId) setCurrentTierID(currentId);
                setMembership(currentId ? m : null);
                // A yearly member starts on the Yearly switch, so their own plan shows
                if (m?.isActive && m?.BillingFrequency === 'yearly') setBillingCycle('yearly');

                if (tiersRes.status === 'fulfilled') {
                    setTrialAvailable(tiersRes.value.data.trialAvailable === true);
                    // Students only see Student-audience tiers — Coach / Enterprise
                    // tiers must not leak into the regular user plans page. A tier
                    // closed to new members (Open for new enrollment off) only
                    // shows for the student who's already on it.
                    // The member's own plan always shows (as Current Plan), also once it's
                    // turned off or isn't a student plan anymore, so the cards compare with it
                    const activeTiers = tiersRes.value.data.tiers.filter(
                        //old t => (t.IsActive === true || t.IsActive === 1) && (t.Audiences || []).includes('Student')
                        //old && (t.OpenForEnrollment === true || t.OpenForEnrollment === 1 || t.TierID === currentId)
                        t => t.TierID === currentId || ((t.IsActive === true || t.IsActive === 1) && (t.Audiences || []).includes('Student')
                            && (t.OpenForEnrollment === true || t.OpenForEnrollment === 1))
                    );
                    const sorted = activeTiers.sort((a, b) => a.Price - b.Price);
                    setTiers(sorted);
                } else {
                    setTiers([]);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [authLoading, user]);

    // Referral card state: the code itself, whether a new one may be issued yet,
    // and the lifetime tally of friends who've used this client's codes.
    const fetchReferral = React.useCallback(async () => {
        try {
            const res = await api.get('/membership/referral/status');
            setReferral(res.data || null);
        } catch (err) {
            console.error('Could not fetch referral status', err);
        }
    }, []);

    useEffect(() => {
        if (authLoading || !user) return;
        fetchReferral();
    }, [authLoading, user, fetchReferral]);

    const handleCopyReferral = () => {
        if (referral?.code) {
            navigator.clipboard.writeText(referral.code);
            setReferralCopied(true);
            setTimeout(() => setReferralCopied(false), 2000);
        }
    };

    // Same button for "get my first code" and "get a new one" — it's one action.
    // The server only issues a new code once the current one has been redeemed,
    // and hides the button entirely while we're still waiting on a friend.
    const handleGetReferralCode = async () => {
        setReferralLoading(true);
        try {
            await api.post('/membership/referral/generate');
            await fetchReferral();
        } catch {
            alert('Could not generate referral code.');
        } finally {
            setReferralLoading(false);
        }
    };

    const describeDiscount = (p) => {
        if (!p) return '';
        if (p.discountType === 'free') return 'free';
        if (p.discountType === 'percent') return `${Number(p.discountValue)}% off`;
        return `$${Number(p.discountValue || 0).toFixed(2)} off`;
    };

    // One box for both kinds of code. Promo codes are checked first; a code that
    // isn't one is tried as a friend's referral code. The two namespaces can't
    // collide — codes are generated unique across both — so this only decides
    // which lookup runs first, never which meaning wins.
    const handleApplyCode = async () => {
        const code = codeInput.trim().toUpperCase();
        if (!code) return;
        setApplying(true);
        setCodeMsg(null);
        try {
            const res = await api.post('/membership/code/preview', { code });
            //setPromo(res.data);
            //const n = res.data?.tiers?.length || 0;
            // Only plans on this page count: a code for coach plans changes none of them
            const n = (res.data?.tiers || []).filter((t) => tiers.some((x) => x.TierID === t.TierID)).length;
            if (n === 0) {
                setCodeMsg({ type: 'bad', text: `${code} isn't for these plans.` });
                return;
            }
            // The code only changes plans on the other side of the Monthly/Yearly
            // switch: flip it so they show
            const covered = (res.data?.tiers || []).filter((t) => tiers.some((x) => x.TierID === t.TierID));
            if (hasYearly && !covered.some((t) => Number(t.Price) === 0 || (t.BillingFrequency || 'monthly') === billingCycle)) {
                setBillingCycle(covered[0].BillingFrequency || 'monthly');
            }
            setPromo(res.data);
            setCodeMsg({
                type: 'ok',
                text: `${code} applied — ${describeDiscount(res.data)} on ${n} plan${n === 1 ? '' : 's'} below.`,
            });
        //} catch {
        } catch (previewErr) {
            // A promo code that can't be used right now (expired, used up, no current
            // plan) says why; only a code that isn't a promo code is tried as a friend's code
            const previewError = previewErr.response?.data?.error;
            if (previewError && previewError !== 'Invalid or inactive code') {
                setCodeMsg({ type: 'bad', text: previewError });
                return;
            }
            try {
                const res = await api.post('/membership/referral/redeem', { referral_code: code });
                setCodeMsg({
                    type: 'ok',
                    text: res.data?.message || 'Referral applied! You and your friend each got +1 week.',
                });
                setCodeInput('');
                fetchReferral();
            } catch (err) {
                setCodeMsg({
                    type: 'bad',
                    text: err.response?.data?.error || "That code isn't valid or has expired.",
                });
            }
        } finally {
            setApplying(false);
        }
    };

    const clearPromo = () => {
        setPromo(null);
        setCodeInput('');
        setCodeMsg(null);
    };

    // The promo's effect on one tier, or null when this tier isn't covered.
    const promoFor = (tierId) => promo?.tiers?.find(t => t.TierID === tierId) || null;
    // With yearly plans on offer, the Monthly/Yearly switch shows and picks which
    // paid plans are listed (free plans always are); without any it's hidden
    const hasYearly = tiers.some((t) => t.BillingFrequency === 'yearly' && Number(t.Price) > 0);
    // Paid by card: another paid plan changes that subscription (never a second checkout)
    const paysByCard = !!membership?.HasSubscription;
    // A licensed student on the free plan: the organization's plan is theirs (Evan, Sep 28)
    const covered = !!membership?.CoveredBy;

    const closeChange = () => {
        if (changing) return;
        setChange(null);
        setChangeError('');
    };

    // Confirmed in the window: a paid plan changes the subscription (now, or when the paid
    // period ends), the free plan is the same as Cancel (Evan, Sep 9: it waits for the end)
    const confirmChange = async () => {
        const { plan: tier, preview, free } = change;
        setChanging(true);
        setChangeError('');
        try {
            if (free) {
                const res = await api.post('/membership/downgrade', { tier_id: tier.TierID });
                navigate('/membership', { state: { notice: !res.data?.scheduled ? `You're on ${tier.TierName} now.`
                    : `You keep ${membership.TierName} until ${day(membership.EndDate)}, then move to ${tier.TierName}.${paysByCard ? " You won't be charged again." : ''}` } });
                return;
            }
            const res = await api.post('/membership/change-plan', {
                tier_id: tier.TierID, proration_date: preview.prorationDate, amount_due: preview.amountDue,
            });
            navigate('/membership', { state: { notice: res.data?.scheduled
                ? `${tier.TierName} starts on ${day(res.data.effectiveOn)}. You keep ${membership.TierName} until then.`
                : `You're on ${tier.TierName} now.${Number(res.data?.amountPaid) > 0 ? ` ${money(res.data.amountPaid)} was charged to your card.` : ''}` } });
        } catch (err) {
            const code = err.response?.data?.code;
            if (code === 'AMOUNT_CHANGED' || code === 'PREVIEW_EXPIRED') {
                // Checked again: the window shows the new amount to confirm
                try {
                    const res = await api.post('/membership/change-plan/preview', { tier_id: tier.TierID });
                    setChange({ plan: tier, preview: res.data });
                    setChangeError(`${err.response.data.error} The amount below is up to date.`);
                } catch (previewErr) {
                    setChangeError(previewErr.response?.data?.error || 'Could not check the price. Try again.');
                }
            } else {
                setChangeError(err.response?.data?.error || 'Could not change your plan. Try again.');
            }
        } finally {
            setChanging(false);
        }
    };

    const handleSubscribe = async (tier) => {
        setSubscribing(tier.TierID);
        try {
            if (!user) {
                alert('You must be logged in to subscribe.');
                setSubscribing(null);
                return;
            }
            // Right after login the user object can carry only `clientid` and no
            // `email` yet — fall back to /user/me so a logged-in user isn't
            // wrongly told to log in.
            let email = user.email;
            if (!email) {
                try {
                    const me = await api.get('/user/me');
                    email = me.data?.email;
                } catch { /* handled by the guard below */ }
            }
            if (!email) {
                alert('We could not read your account email. Please refresh and try again.');
                setSubscribing(null);
                return;
            }

            // A $0 tier with no trial (Biz-Free) has no Stripe price behind it —
            // same rule Signup.js's stage-3 picker uses — so it bypasses Stripe
            // entirely instead of hitting create-checkout-session with an empty
            // price_id.
            // A 'free' (full grant) promo code covering this tier: the code alone
            // granted nothing — this confirmation is what actually claims it.
            //if (promo && promo.discountType === 'free' && promoFor(tier.TierID)) {
            // (a $0 plan doesn't need the code: the usual switch keeps a paid plan until it ends)
            if (promo && promo.discountType === 'free' && promoFor(tier.TierID) && Number(tier.Price) > 0) {
                await api.post('/membership/code/redeem', { code: promo.code, tier_id: tier.TierID });
                window.location.href = '/membership';
                return;
            }

            const isFreeForever = Number(tier.Price) === 0 && !tier.TrialDays;
            if (isFreeForever) {
                // Downgrade to Free. The backend schedules it for the end of the
                // current plan's period — the member keeps their current features
                // until then (whatever tier they're on, paid or free) and can
                // switch back for free before then.
                //old const ok = window.confirm(
                //old `Switch your plan to ${tier.TierName}?\n\nYou'll keep your current features until your plan's current period ends, and can switch back anytime before then at no charge.`
                //old );
                //old if (!ok) { setSubscribing(null); return; }
                //old await api.post('/membership/downgrade', { tier_id: tier.TierID });
                //old window.location.href = '/membership';
                // Asked in the window first, with the day the plan ends (one way out, like Cancel)
                setChangeError('');
                setChange({ plan: tier, free: true });
                return;
            }

            // Someone who pays by card changes that plan instead: the window says what's
            // charged today, or when a cheaper plan starts (a second checkout charged the
            // new plan in full and stopped the one already paid for)
            if (paysByCard) {
                try {
                    const res = await api.post('/membership/change-plan/preview', { tier_id: tier.TierID });
                    setChangeError('');
                    setChange({ plan: tier, preview: res.data });
                    return;
                } catch (previewErr) {
                    // Not paid by card anymore (the subscription just ended): bought at checkout below
                    if (previewErr.response?.data?.code !== 'NO_SUBSCRIPTION') throw previewErr;
                }
            }

            const response = await api.post('/membership/create-checkout-session', {
                price_id: tier.PaymentLink,
                tier_id: tier.TierID,
                // A percent/amount code applied above lowers this checkout
                promo_code: promo && promo.discountType !== 'free' && promoFor(tier.TierID) ? promo.code : undefined,
                customer_email: email
            });
            const { checkout_url } = response.data;
            if (checkout_url) {
                window.location.href = checkout_url;
            } else {
                alert('Could not start checkout.');
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error starting checkout.');
        } finally {
            setSubscribing(null);
        }
    };

    // [Upgrade my plan] on My Coaching -> Programs (JJ, Nov 2): the plan its organization's licenses give, at its own
    // price. Once the plans are in, it goes on as its card's button would (to checkout, or the window for a plan paid
    // by card; nothing for the plan they're on). The plan comes off the URL first, so Back from checkout or a refresh
    // doesn't start it again
    useEffect(() => {
        if (loading || !planFromLink.current) return;
        const tier = tiers.find((t) => String(t.TierID) === planFromLink.current);
        planFromLink.current = null;
        const next = new URLSearchParams(searchParams);
        next.delete('plan');
        setSearchParams(next, { replace: true });
        if (!tier) return;
        // its card shows (a yearly plan is under Yearly)
        if (hasYearly && Number(tier.Price) > 0) setBillingCycle(tier.BillingFrequency || 'monthly');
        if (tier.TierID !== currentTierID && membership?.PendingTierID !== tier.TierID) handleSubscribe(tier);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    if (loading) {
        //old return (
        //old <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
        //old <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        //old <span className={`ml-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading membership plans...</span>
        //old </div>
        //old );
        // The plans page's skeleton (the same layout as the coach plans page)
        return <CoachPlansSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`max-w-5xl mx-auto py-10 px-4 space-y-8 ${isDarkMode ? 'bg-surface' : ''}`}>
            {/* Back — return to wherever the user came from (My Membership, the CSI
                result page, etc.); fall back to My Membership on a direct visit. */}
            <button
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all ${isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/membership'))}
            >
                <ArrowLeft size={18} /> Back
            </button>

            {/* Page heading */}
            <div>
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Choose a Membership Plan</h1>
                <p className={`mt-2 text-sm max-w-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {/* old <span className="font-semibold">Lucky you!</span> We're currently offering free trial access for 60 days during our Beta, ending Oct. 1st. Please be sure to select either the <span className="font-semibold">Pro - Beta</span> or <span className="font-semibold">Pro - Beta (annual)</span> plan. You can cancel your membership and future payments anytime. */}
                    {/* The Beta offer is over (Alicia, Sep 4); each card says its own free trial */}
                    You can cancel your membership and future payments anytime.
                </p>
            </div>

            {/* A licensed student: the organization's plan is theirs while the license lasts (Evan, Sep 28) */}
            {covered && (
                <div className={`rounded-xl border p-4 text-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-primary-tint border-primary/20 text-ink'}`}>
                    <span className="font-semibold">{membership.CoveredBy.OrgName}</span> covers your plan ({membership.CoveredBy.TierName}) while your license is active. You don't pay for it.
                </div>
            )}

            {/* Billing Cycle Toggle */}
            {hasYearly && (
            <div className="flex justify-center">
                <div className={`inline-flex rounded-full p-1 text-sm font-semibold ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100'}`}>
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-6 py-2 rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-primary text-white shadow-sm' : isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-6 py-2 rounded-full transition-all ${billingCycle === 'yearly' ? 'bg-primary text-white shadow-sm' : isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Yearly
                    </button>
                </div>
            </div>
            )}

            {/* Refer a Friend + code entry — one card: share your own code above,
                enter someone else's below. Both kinds of code use the same box. */}
            <div className={`rounded-2xl border p-5 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Gift className="text-primary" size={22} />
                        <div>
                            <p className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Refer a Friend</p>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {referral?.code
                                    ? 'Share this code — you both get +1 week.'
                                    : 'Both you and your friend get +1 week added to your membership!'}
                            </p>
                        </div>
                    </div>
                    {/* The code stays on the card once issued. The button is the same
                        action either way ("get my first" / "get a new one"), and is
                        simply absent while we're waiting on a friend to use the
                        current code — no button is explanation enough. */}
                    {referral?.code ? (
                        <div className="flex items-center gap-2">
                            <span className={`font-mono text-base font-bold tracking-widest px-4 py-2 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-600 text-primary' : 'bg-slate-50 border-slate-200 text-primary-dark'}`}>
                                {referral.code}
                            </span>
                            <Button variant="secondary" size="sm" onClick={handleCopyReferral}>
                                {referralCopied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                                {referralCopied ? 'Copied' : 'Copy'}
                            </Button>
                        </div>
                    ) : referral?.canGenerate !== false ? (
                        <Button variant="primary" size="sm" loading={referralLoading} onClick={handleGetReferralCode}>
                            <Gift size={16} /> Get my code
                        </Button>
                    ) : null}
                </div>

                {/* Lifetime tally — independent of whether a code is currently out,
                    so it stays put right after rotating to a fresh one. */}
                {referral?.usedCount > 0 && (
                    <p className="mt-3 flex items-start gap-2 text-sm font-medium text-success">
                        <Check size={16} className="shrink-0 mt-0.5" />
                        <span>
                            <strong>{referral.usedCount} friend{referral.usedCount === 1 ? '' : 's'} used your code</strong>
                            {' '}— +{referral.weeksEarned} week{referral.weeksEarned === 1 ? '' : 's'} added to your membership.
                        </span>
                    </p>
                )}

                <div className="border-t border-border my-4" />

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className={`font-bold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Have a code?</p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Enter a friend&rsquo;s referral code or a promo code.</p>
                    </div>
                    <div className="flex gap-2">
                        <input
                            value={codeInput}
                            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCode(); }}
                            placeholder="ENTER CODE"
                            disabled={!!promo}
                            className={`w-44 px-3 py-2 rounded-xl border font-mono font-semibold tracking-widest uppercase disabled:opacity-60 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-100' : 'border-slate-300'}`}
                        />
                        {promo ? (
                            <Button variant="secondary" size="sm" onClick={clearPromo}>Remove</Button>
                        ) : (
                            <Button variant="primary" size="sm" loading={applying} disabled={!codeInput.trim()} onClick={handleApplyCode}>
                                {applying ? 'Applying\u2026' : 'Apply'}
                            </Button>
                        )}
                    </div>
                </div>

                {codeMsg && (
                    <p className={`mt-3 text-sm font-medium ${codeMsg.type === 'ok' ? 'text-success' : 'text-error'}`}>{codeMsg.text}</p>
                )}
            </div>

            {/* Plans in columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                {/* {tiers.map((tier, idx) => { */}
                {tiers.filter((t) => !hasYearly || Number(t.Price) === 0 || (t.BillingFrequency || 'monthly') === billingCycle).map((tier, idx) => {
                    // "Most Popular" is now an admin-set flag per tier (not a
                    // hardcoded middle-card rule).
                    const isPro = tier.IsMostPopular === true || tier.IsMostPopular === 1;
                    //old const isCurrentPlan = currentTierID === tier.TierID;
                    // A covered student's own free plan isn't what they have (the organization's plan is)
                    const isOwnPlan = currentTierID === tier.TierID;
                    const isCurrentPlan = isOwnPlan && !covered;
                    //old const currentIdx = tiers.findIndex(t => t.TierID === currentTierID);
                    //const isUpgrade = currentTierID && !isCurrentPlan && idx > currentIdx;
                    //const isDowngrade = currentTierID && !isCurrentPlan && idx < currentIdx;
                    // Compared in the full list: the Monthly/Yearly switch can hide cards
                    const tierIdx = tiers.indexOf(tier);
                    //old const isUpgrade = currentTierID && !isCurrentPlan && tierIdx > currentIdx;
                    //old const isDowngrade = currentTierID && !isCurrentPlan && tierIdx < currentIdx;
                    // By price per month (or by the card-paying rule), not the list's order
                    const action = actionLabel(membership, tier, { paysByCard, covered });
                    const isDowngrade = action === 'Downgrade';
                    // Already picked for when the paid period ends (a cheaper plan, or the free plan)
                    const startsLater = !isOwnPlan && membership?.PendingTierID === tier.TierID;
                    return (
                        <Card
                            key={tier.TierID}
                            variant={isPro ? 'elevated' : 'standard'}
                            className={`flex flex-col relative ${promoFor(tier.TierID) ? 'ring-2 ring-primary/40' : isCurrentPlan ? 'ring-2 ring-primary/25' : ''}`}
                        >
                            {/* Top banner */}
                            <CardBandedHeader className="relative">
                                {/* Badges — top right */}
                                <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                                    {isCurrentPlan && <Badge variant="primary">✓ Current Plan</Badge>}
                                    {isPro && !isCurrentPlan && <Badge variant="primary">Most Popular</Badge>}
                                </div>
                                <p className="text-white/45 text-xs font-bold uppercase tracking-widest mb-1">Plan</p>
                                <h2 className="text-2xl font-bold text-white pr-24">{tier.TierName}</h2>
                            </CardBandedHeader>

                            {/* Body */}
                            <CardBody className="flex-1 flex flex-col gap-5">
                                {/* Price */}
                                <div className="text-center">
                                    {promoFor(tier.TierID) ? (
                                        <>
                                            <div className="text-sm font-semibold text-ink-soft line-through">${Number(tier.Price).toFixed(2)}</div>
                                            <div className="flex items-baseline justify-center gap-1">
                                                <span className="text-4xl font-extrabold text-primary">${Number(promoFor(tier.TierID).DiscountedPrice).toFixed(2)}</span>
                                                {/* <span className="text-sm font-semibold text-ink-soft">/mo</span> */}
                                                <span className="text-sm font-semibold text-ink-soft">{tier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}</span>
                                            </div>
                                            {/* The code lowers the payments made before it ends */}
                                            {promoFor(tier.TierID).DiscountEndsOn && (
                                                <div className="mt-1 text-xs font-semibold text-ink-soft">
                                                    until {new Date(`${promoFor(tier.TierID).DiscountEndsOn}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, then ${Number(tier.Price).toFixed(2)}{tier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}
                                                </div>
                                            )}
                                            <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-success/10 text-success">
                                                {promo.code} &middot; {describeDiscount(promo)}
                                            </span>
                                        </>
                                    ) : (
                                    <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-4xl font-extrabold text-primary">${Number(tier.Price).toFixed(2)}</span>
                                        {/* <span className="text-sm font-semibold text-ink-soft">/mo</span> */}
                                        <span className="text-sm font-semibold text-ink-soft">{tier.BillingFrequency === 'yearly' ? '/yr' : '/mo'}</span>
                                    </div>
                                    )}
                                    {/* The plan's own free trial, only for someone who gets it (a first subscription) */}
                                    {trialAvailable && Number(tier.Price) > 0 && Number(tier.TrialDays) > 0 && !isOwnPlan && (
                                        <p className="mt-1 text-xs font-semibold text-primary">
                                            Free for {tier.TrialDays} days, then billed {isYearly(tier) ? 'yearly' : 'monthly'}
                                        </p>
                                    )}
                                    {tier.TierDescription && (
                                        <p className="mt-2 text-sm text-ink-soft">{tier.TierDescription}</p>
                                    )}
                                </div>

                                {/* Divider */}
                                <div className="border-t border-border" />

                                {/* Features */}
                                <ul className="space-y-2 flex-1">
                                    {MEMBERSHIP_FEATURES.map((feature, i) => {
                                        //const { included, badge } = getFeaturesForTier(idx)[i];
                                        // the CSI line follows what this plan really unlocks
                                        const { included, badge } = withTierAccess(getFeaturesForTier(tierIdx), tier)[i];
                                        return (
                                            <li key={`feature-${idx}-${i}`} className={`flex items-center gap-2 text-sm ${included ? 'text-ink' : 'text-ink-soft'}`}>
                                                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${included ? 'bg-primary-tint text-primary' : 'bg-section text-ink-soft'}`}>
                                                    {included ? '✓' : '✗'}
                                                </span>
                                                <span className={included ? '' : 'line-through'}>{feature}</span>
                                                {included && badge && (
                                                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary-tint text-primary">
                                                        {badge}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>

                                {/* CTA Button */}
                                {/* (none on a covered student's own free plan: there's nothing to pick) */}
                                {!(isOwnPlan && covered) && (
                                <Button
                                    variant={isCurrentPlan ? 'secondary' : isDowngrade ? 'secondary' : 'primary'}
                                    className={`w-full mt-2 ${isCurrentPlan ? 'bg-primary-tint text-primary border border-primary/30 cursor-default' : ''}`}
                                    //old onClick={() => !isCurrentPlan && handleSubscribe(tier, idx)}
                                    //old disabled={isCurrentPlan}
                                    onClick={() => !isCurrentPlan && !startsLater && handleSubscribe(tier, idx)}
                                    disabled={isCurrentPlan || startsLater}
                                    loading={subscribing === tier.TierID}
                                >
                                    <CreditCard size={18} />
                                    {isCurrentPlan
                                        ? 'Current Plan'
                                        : subscribing === tier.TierID
                                        ? 'Processing...'
                                        //old : isUpgrade
                                        //old ? 'Upgrade'
                                        //old : isDowngrade
                                        //old ? 'Downgrade'
                                        //old : 'Subscribe'}
                                        : startsLater
                                        ? `Starts ${day(membership.EndDate)}`
                                        : action}
                                </Button>
                                )}
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            {/* Before a plan changes: what's charged today, or when it starts */}
            {change && (
                <PlanChangeConfirm
                    change={change}
                    membership={membership}
                    paysByCard={paysByCard}
                    // With no plan at all the free plan starts now
                    freeNow={!membership}
                    // A code entered here is for a new subscription, not a change to the one paid by card
                    // (a discount that subscription already has stays on it)
                    promoNote={!change.free && promo && promo.discountType !== 'free' && promoFor(change.plan.TierID)
                        ? `${promo.code} is for new subscriptions, so it isn't used for this change.` : null}
                    error={changeError}
                    busy={changing}
                    onClose={closeChange}
                    onConfirm={confirmChange}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
}
